import { useEffect, useRef, forwardRef, useImperativeHandle, ElementType } from 'react';
import { Box, Typography, Alert, Chip } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import LocalPostOfficeIcon from '@mui/icons-material/LocalPostOffice';
import { useLanguage } from '../../../../context/LanguageContext';
import { ChannelType } from './CampaignDefinitionStep';
import EmailUnlayerSection, { EmailContent, EmailUnlayerHandle, EmailUnlayerInitialContent } from './EmailUnlayerSection';
import SmsCreationSection from './SmsCreationSection';
import TikeoCreationSection from './TikeoCreationSection';
import CashdeskCreationSection from './CashdeskCreationSection';
import CourrierCreationSection from './CourrierCreationSection';
import { readEmailSubject } from './creationPayload';
import useLatestRef from './creation/shared/useLatestRef';
import { CreationPayload, InitialPayload, readOption, readString } from './creation/shared/payloadReaders';
import { alpha, useTheme } from '@mui/material/styles';

interface CampaignCreationStepProps {
  channel?: ChannelType;
  onHasContent?: (v: boolean) => void;
  offerCount?: number;
  offerTitles?: string[];
  onAddOffer?: () => void;
  onOfferUsed?: (offerIndex: number) => void;
  onOfferIndicesChange?: (indices: Set<number>) => void;
  onOfferRemoved?: (offerIndex: number) => void;
  initialPayload?: InitialPayload;
  onPayloadChange?: (payload: CreationPayload) => void;
}

export interface CreationStepHandle {
  getEmailContent: () => Promise<EmailContent | null>;
}

// Couleur d'identité du canal : palette `channel` du thème
const CHANNEL_META: Record<Exclude<ChannelType, ''>, { label: string; Icon: ElementType }> = {
  courrier: { label: 'Courrier', Icon: LocalPostOfficeIcon },
  email: { label: 'Email', Icon: EmailIcon },
  sms: { label: 'SMS', Icon: SmsIcon },
  wallet: { label: 'Wallet', Icon: AccountBalanceWalletIcon },
  cashdesk: { label: 'Caisse', Icon: PointOfSaleIcon },
};

const readEmailInitialContent = (payload: InitialPayload): EmailUnlayerInitialContent | null => {
  const html = readString(payload, 'emailHtml') || undefined;
  const design = payload?.emailDesign && typeof payload.emailDesign === 'object' ? payload.emailDesign : undefined;
  const subject = readEmailSubject(payload);
  if (!html && !design && !subject) return null;
  return { mode: readOption(payload, 'emailEditorMode', ['unlayer', 'html'] as const, 'unlayer'), html, design, subject };
};

const CampaignCreationStep = forwardRef<CreationStepHandle, CampaignCreationStepProps>((props, ref) => {
  const { channel, onHasContent, offerCount = 1, offerTitles, onAddOffer, onOfferUsed, onOfferIndicesChange, onOfferRemoved, initialPayload, onPayloadChange } = props;
  const { t } = useLanguage();
  const emailRef = useRef<EmailUnlayerHandle>(null);
  const onHasContentRef = useLatestRef(onHasContent);
  const theme = useTheme();
  const meta = channel ? CHANNEL_META[channel] : null;
  const channelColor = channel ? theme.palette.channel[channel] : '';
  const offerProps = { offerCount, onAddOffer, onOfferUsed };

  useEffect(() => {
    if (channel && channel !== 'email') onHasContentRef.current?.(true);
  }, [channel, onHasContentRef]);

  useImperativeHandle(ref, () => ({
    getEmailContent: () => emailRef.current?.getContent() ?? Promise.resolve(null),
  }), []);

  const renderContent = () => {
    switch (channel) {
      case 'email':
        return (
          <EmailUnlayerSection
            ref={emailRef}
            onSave={() => onHasContent?.(true)}
            {...offerProps}
            offerTitles={offerTitles}
            onOfferIndicesChange={onOfferIndicesChange}
            initialContent={readEmailInitialContent(initialPayload)}
          />
        );
      case 'sms':
        return <SmsCreationSection {...offerProps} offerTitles={offerTitles} initialPayload={initialPayload} onPayloadChange={onPayloadChange} />;
      case 'wallet':
        return <TikeoCreationSection initialPayload={initialPayload} onPayloadChange={onPayloadChange} />;
      case 'courrier':
        return <CourrierCreationSection initialPayload={initialPayload} onPayloadChange={onPayloadChange} />;
      case 'cashdesk':
        return (
          <CashdeskCreationSection
            {...offerProps}
            onOfferIndicesChange={onOfferIndicesChange}
            onOfferRemoved={onOfferRemoved}
            initialPayload={initialPayload}
            onPayloadChange={onPayloadChange}
          />
        );
      default:
        return (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Veuillez d&apos;abord sélectionner un canal à l&apos;étape <strong>Définition</strong>.
          </Alert>
        );
    }
  };

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('pages.marketing.communication.creation')}
        </Typography>
        {meta && (
          <Chip
            icon={<meta.Icon sx={{ fontSize: '0.9rem !important' }} />}
            label={meta.label}
            size="small"
            sx={{
              backgroundColor: alpha(channelColor, 0.09),
              color: channelColor,
              border: `1px solid ${alpha(channelColor, 0.25)}`,
              fontWeight: 600,
              fontSize: '0.75rem',
              '& .MuiChip-icon': { color: channelColor },
            }}
          />
        )}
      </Box>

      {renderContent()}
    </Box>
  );
});

CampaignCreationStep.displayName = 'CampaignCreationStep';

export default CampaignCreationStep;
